import type { ProductLine, Standing } from './standings';
import { cr, pc } from './format';

const CORE_COLORS = ['#2fb88a', '#5fe0b0', '#11876a', '#a6e8b0', '#3fa7a0', '#7fd1c7', '#1f6f5c', '#c9f2c9'];
const MANDATORY_COLORS = ['#ef7a1a', '#f2b134', '#d8261a'];

function Group({
  title,
  sub,
  total,
  totalAll,
  items,
  colors,
}: {
  title: string;
  sub?: string;
  total: number;
  totalAll: number;
  items: ProductLine[];
  colors: string[];
}) {
  const colored = items.map((it, i) => ({ ...it, color: colors[i % colors.length] }));
  const positive = colored.filter((it) => it.cr > 0).sort((a, b) => b.cr - a.cr);
  const negative = colored.filter((it) => it.cr < 0);
  const empty = colored.filter((it) => it.cr === 0);
  const base = positive.reduce((s, it) => s + it.cr, 0);

  return (
    <div className="sk-split-g">
      <div className="sk-split-h">
        <div>
          <div className="t">{title}</div>
          {sub && <div className="s">{sub}</div>}
        </div>
        <div className="v">
          {cr(total)}
          {totalAll > 0 && total > 0 && <small>{pc(total / totalAll)} of total</small>}
        </div>
      </div>
      {base > 0 ? (
        <>
          <div className="sk-split-bar" role="img" aria-label={`${title} by product`}>
            {positive.map((it) => (
              <div key={it.key} style={{ width: (it.cr / base) * 100 + '%', background: it.color }} title={it.label} />
            ))}
          </div>
          <ul className="sk-split-list">
            {positive.map((it) => (
              <li key={it.key}>
                <i style={{ background: it.color }} />
                <span className="n">{it.label}</span>
                <span className="c">{cr(it.cr)}</span>
                <span className="p">{pc(it.cr / base)}</span>
              </li>
            ))}
            {negative.map((it) => (
              <li key={it.key} className="neg">
                <i />
                <span className="n">{it.label}</span>
                <span className="c">{cr(it.cr)}</span>
                <span className="p">net out</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="sk-split-none">No {title.toLowerCase()} business yet.</p>
      )}
      {base > 0 && empty.length > 0 && (
        <p className="sk-split-none">Nothing yet in {empty.map((it) => it.label).join(' · ')}</p>
      )}
    </div>
  );
}

/** An RM's business split by product, Core wealth and Mandatory side by side. */
export default function ProductSplit({ p }: { p: Standing }) {
  // Shares are of positive business only, so a net redemption in one bucket
  // can't push the other past 100%.
  const base = Math.max(p.coreCr, 0) + Math.max(p.cat2Cr, 0);
  return (
    <div className="sk-split">
      <Group
        title="Core wealth"
        total={p.coreCr}
        totalAll={base}
        items={p.products.core}
        colors={CORE_COLORS}
      />
      <Group
        title="Mandatory"
        sub="Category II · NCD · Corporate FD"
        total={p.cat2Cr}
        totalAll={base}
        items={p.products.mandatory}
        colors={MANDATORY_COLORS}
      />
    </div>
  );
}
